import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import { LanguageRecord, fetchLanguageByUuid } from '../utils/formsApi';

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  return String(value);
};

const LanguageDetailsPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [language, setLanguage] = useState<LanguageRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    if (!uuid) {
      setError('A language UUID is required.');
      setIsLoading(false);
      return () => { isMounted = false; };
    }

    fetchLanguageByUuid(uuid)
      .then((selectedLanguage) => {
        if (isMounted) setLanguage(selectedLanguage);
      })
      .catch((requestError) => {
        console.error('Failed to fetch language details:', requestError);
        if (isMounted) setError(requestError instanceof Error ? requestError.message : 'Failed to load the language details.');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => { isMounted = false; };
  }, [uuid]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'left' }}>
      <Typography component={Link} to="/languages" color="primary" sx={{ textDecoration: 'none' }}>
        ← Available Languages
      </Typography>
      <Typography variant="h4" sx={{ mt: 2, mb: 3 }}>Language Details</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {isLoading && <CircularProgress aria-label="Loading language details" />}
      {language && (
        <Paper sx={{ p: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(language).map(([field, value]) => {
              const displayValue = formatValue(value);
              const isStructuredValue = (typeof value === 'object' && value !== null) || field === 'content';
              return (
                <TextField
                  key={field}
                  label={field}
                  value={displayValue}
                  multiline={isStructuredValue || displayValue.length > 120}
                  minRows={isStructuredValue ? 8 : undefined}
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                  sx={isStructuredValue ? { '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } } : undefined}
                />
              );
            })}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default LanguageDetailsPage;
